import { type ReactNode } from 'react'

import { cn } from '../lib/cn'

/**
 * Empty state — ported from `FgEmpty` in `designs/system.jsx` (~646).
 *
 * FULLY DERIVED, and M16 says so at the atom: the Figma file draws no empty
 * state anywhere (audit gap G31). The dashed `--line-2` border, the centred
 * column and the type ramp are ours; they are recorded here so a later reader
 * does not go looking for an artboard that was never drawn.
 *
 * The app had five of these hand-rolled across pages before this component,
 * each with slightly different padding and a different heading level. One
 * shape, one heading level, one set of spacing.
 */
export interface EmptyStateProps {
  /** Short statement of what is not here. Rendered as the heading. */
  title: string
  /** One line on what to do about it. */
  body?: ReactNode
  /**
   * Optional recovery action — a `Button`, usually. Omitted when there is
   * nothing useful to offer; an empty state with a dead button is worse than
   * one without.
   */
  action?: ReactNode
  /**
   * Heading level for `title`. Defaults to `h2`. A page that already has an
   * `h2` above the list passes `h3` so the outline stays legal.
   */
  headingLevel?: 'h2' | 'h3'
  className?: string
}

export function EmptyState({
  title,
  body,
  action,
  headingLevel: Heading = 'h2',
  className,
}: EmptyStateProps): ReactNode {
  return (
    <div
      className={cn(
        // PORTED — dashed --line-2 border at --radius-r3, centred, generous
        // vertical padding. This is the one place a dashed border is correct:
        // it reads as "a container that could hold something" rather than a
        // card that does.
        'flex flex-col items-center rounded-r3 border border-dashed border-line-2 px-6 py-12 text-center',
        className,
      )}
    >
      <Heading className="text-[18px] font-medium leading-7 text-ink-1">
        {title}
      </Heading>
      {body ? (
        <p className="mt-2 max-w-prose text-[14px] leading-5 text-ink-3">
          {body}
        </p>
      ) : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  )
}
